import React, { useState } from 'react';
import { Copy, AlertCircle, Download, Zap } from 'lucide-react';

const JSONFormatter = () => {
  const [input, setInput] = useState('{\n  "name": "John",\n  "age": 30,\n  "city": "New York"\n}');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [minified, setMinified] = useState(false);

  const handleFormat = () => {
    try {
      setError('');
      const parsed = JSON.parse(input);
      if (minified) {
        setOutput(JSON.stringify(parsed));
      } else {
        setOutput(JSON.stringify(parsed, null, 2));
      }
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      setOutput('');
    }
  };

  const handleValidate = () => {
    try {
      JSON.parse(input);
      setError('');
      setOutput('✓ Valid JSON');
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      setOutput('');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadJSON = () => {
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(output));
    element.setAttribute('download', 'formatted.json');
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 font-mono">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Inter:wght@500;600;700&display=swap');
        
        * {
          font-family: 'JetBrains Mono', monospace;
        }
        
        h1, h2 {
          font-family: 'Inter', sans-serif;
          letter-spacing: -0.02em;
        }

        textarea {
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid rgba(71, 85, 105, 0.5);
          transition: all 0.2s ease;
          font-family: 'JetBrains Mono', monospace;
        }

        textarea:focus {
          background: rgba(15, 23, 42, 0.9);
          border-color: rgba(59, 130, 246, 0.8);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
          outline: none;
        }

        button {
          transition: all 0.2s ease;
        }

        button:hover {
          transform: translateY(-1px);
        }

        button:active {
          transform: translateY(0);
        }
      `}</style>

      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">JSON Formatter</h1>
          <p className="text-slate-400 text-sm">Format, validate, and minify JSON instantly</p>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Input JSON</label>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="w-full h-80 p-4 rounded-lg text-slate-100 placeholder-slate-500 resize-none text-sm leading-relaxed focus:ring-0"
              placeholder="Paste your JSON here..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Output</label>
            <textarea
              value={output}
              readOnly
              className="w-full h-80 p-4 rounded-lg text-slate-100 bg-slate-700/20 border border-slate-600/50 resize-none text-sm leading-relaxed"
              placeholder="Formatted JSON will appear here..."
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-600/50 rounded-lg mb-6">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <div className="flex gap-3 mb-6 flex-wrap">
          <button
            onClick={handleFormat}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold uppercase tracking-wide hover:bg-blue-700 transition-all"
          >
            <Zap className="w-4 h-4 inline mr-2" />
            Format
          </button>

          <button
            onClick={handleValidate}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold uppercase tracking-wide hover:bg-green-700 transition-all"
          >
            Validate
          </button>

          <button
            onClick={() => setMinified(!minified)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold uppercase tracking-wide transition-all ${
              minified
                ? 'bg-purple-600 text-white hover:bg-purple-700'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {minified ? 'Minified' : 'Pretty'}
          </button>

          {output && (
            <>
              <button
                onClick={copyToClipboard}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm font-semibold uppercase tracking-wide hover:bg-slate-600 transition-all"
              >
                {copied ? '✓ Copied' : <Copy className="w-4 h-4 inline mr-2" />}
                Copy
              </button>

              <button
                onClick={downloadJSON}
                className="px-4 py-2 bg-slate-700 text-slate-300 rounded-lg text-sm font-semibold uppercase tracking-wide hover:bg-slate-600 transition-all"
              >
                <Download className="w-4 h-4 inline mr-2" />
                Download
              </button>
            </>
          )}
        </div>

        <div className="text-center text-xs text-slate-500">
          <p>JSON Formatter • Instant validation and formatting • v1.0</p>
        </div>
      </div>
    </div>
  );
};

export default JSONFormatter;